import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, useRouter, useRouterState, HeadContent, Scripts, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import appCss from "../styles.css?inline";
import { PublicHeader } from "@/components/site/PublicHeader";
import { PublicFooter } from "@/components/site/PublicFooter";
import { LangProvider, useT } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { detectEnHost, rememberEnHost, rememberedEnHost } from "@/lib/en-host";
import { isEnDomainPath, SERVE_ENGLISH_SITE } from "@/lib/locale-paths";
import { MissingPage } from "@/components/MissingPage";
import { isGoneLegacyPath } from "@/lib/redirects";
import { EnShell } from "@/components/en/EnShell";
import { ConsentBanner } from "@/components/ConsentBanner";
import { getInitialConsent } from "@/lib/consent.functions";
import { categoryAllowed } from "@/lib/cookie-consent";
import { trackAnalyticsPageView } from "@/lib/consented-analytics";

function NotFoundComponent() {
  const { t } = useT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Mark the render as a 404 so the analytics beacon skips it: non-existent
  // URLs (bot probes, stale links) must not appear as "top content".
  useEffect(() => {
    (window as unknown as { __lv404?: boolean }).__lv404 = true;
    return () => {
      (window as unknown as { __lv404?: boolean }).__lv404 = false;
    };
  }, []);
  return <MissingPage gone={isGoneLegacyPath(pathname)} title={t("err.404_title")} body={t("err.404_body")} homeLabel={t("err.home")} />;
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const { t } = useT();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl">{t("err.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("err.body")}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-full bg-ink px-5 py-2 text-[11px] uppercase tracking-[0.2em] font-bold text-paper hover:bg-moss transition-colors"
          >
            {t("err.retry")}
          </button>
          <a
            href="/"
            className="rounded-full border border-ink/20 px-5 py-2 text-[11px] uppercase tracking-[0.2em] font-bold hover:border-ink transition-colors"
          >
            {t("err.go_home")}
          </a>
        </div>
      </div>
    </div>
  );
}

// Brand identity (site name, title, URL, og:image, locale) comes from the
// single locale config in @/lib/brand so the two brands can never diverge.

// English-language pages that live outside the /en tree (legacy URLs kept
// for backlinks). They carry the English brand like any /en page.
const EN_LEGACY_PATHS = new Set([
  "/archive",
  "/glossary",
  "/ai-society",
  "/climate-systems",
  "/human-futures",
  "/sustainability-critique",
]);

function isEnRoute(
  matches?: Array<{
    routeId?: string;
    pathname?: string;
    loaderData?: unknown;
    context?: unknown;
  }>,
) {
  if (!matches) return false;
  // The English domain serves English at its root URL (no /en prefix).
  if (
    matches.some(
      (m) =>
        (m.loaderData as { isEnHost?: boolean } | undefined)?.isEnHost === true ||
        (m.context as { isEnHost?: boolean } | undefined)?.isEnHost === true,
    )
  ) {
    return true;
  }
  return matches.some((m) => {
    const id = m.routeId ?? "";
    const path = (m.pathname ?? "").replace(/\/+$/, "") || "/";
    if (isEnDomainPath(path)) return true;
    const root = "/" + path.split("/")[1];
    return EN_LEGACY_PATHS.has(root);
  });
}

function isHomePath(matches?: Array<{ pathname?: string }>) {
  const last = matches?.[matches.length - 1]?.pathname ?? "";
  const clean = last.replace(/\/+$/, "") || "/";
  return clean === "/";
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient; isEnHost?: boolean }>()({
  beforeLoad: async () => ({ isEnHost: rememberEnHost(await detectEnHost()) }),
  loader: async ({ context }) => ({ isEnHost: context.isEnHost, consent: await getInitialConsent() }),
  head: ({ matches }) => {
    const isEn = SERVE_ENGLISH_SITE && (isEnRoute(matches) || (rememberedEnHost() && isHomePath(matches)));
    const brand = BRAND[isEn ? "en" : "tr"];
    const siteName = brand.siteName;
    const locale = brand.ogLocale;
    

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { name: "theme-color", content: "#f5f1e8" },
        { name: "format-detection", content: "telephone=no" },
        { name: "referrer", content: "strict-origin-when-cross-origin" },
        { name: "google-site-verification", content: "n3qABRqhy9Ch5daOg0Z3EozE6h79kGaJWIJ8YcFuB6A" },
        { name: "google-site-verification", content: "OAqjTqQlIk0WGR3jiswPM5RJmycVeOtq_YZv57Nq1_g" },
        // Page-specific title/description/og:title/og:description/og:url live
        // on each leaf route only — emitting defaults here duplicated them.
        { name: "author", content: "Arda Öztaşkın" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: siteName },
        { property: "og:locale", content: locale },
        // og:locale:alternate is NOT emitted sitewide: it must only appear on
        // pages that really have a translated counterpart, so it stays in sync
        // with the page-level rel="alternate" hreflang links. Leaf routes with
        // a counterpart add it themselves.
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:creator", content: "@ardaoztaskin" },
      ],
      links: [
        { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
        { rel: "alternate icon", href: "/favicon.svg" },
        { rel: "apple-touch-icon", href: "/favicon.svg" },
        {
          rel: "alternate",
          type: "application/rss+xml",
          title: `${siteName} — RSS`,
          href: `${brand.siteUrl}/rss.xml`,
        },
      ],

      // WebSite / Person / Organization JSON-LD lives on the homepage routes
      // (src/routes/index.tsx, src/routes/en.index.tsx) so content pages emit
      // only their own schemas.
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent as never,
});

function useHtmlLang() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (!SERVE_ENGLISH_SITE) return "tr";
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (isEnDomainPath(clean)) return "en";
  // English domain root serves English content.
  if (clean === "/" && (detectEnHost() || rememberedEnHost())) return "en";
  return EN_LEGACY_PATHS.has("/" + clean.split("/")[1]) ? "en" : "tr";
}

function RootShell({ children }: { children: React.ReactNode }) {
  const htmlLang = useHtmlLang() ?? "tr";
  const consent = Route.useLoaderData()?.consent ?? null;
  return (
    <html lang="tr" {...(htmlLang === "en" ? { lang: "en" } : {})}>
      <head>
        {/* App CSS is inlined into the HTML to eliminate the render-blocking
            external stylesheet request. The CSS is small (~18 KiB) and inlining
            it removes a critical-path round-trip, improving LCP/FCP. Previous
            async-load attempts caused unstyled flash + CLS ~0.7. */}
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
        {/* CSP is delivered as an HTTP header from src/start.ts (securityHeadersMiddleware).
            frame-ancestors is ignored in <meta> per spec, so we don't duplicate it here. */}
        <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
        {/* Search Console ownership proof for the *.lovable.app project host.
            Kept as a raw tag so it isn't deduped against the other
            google-site-verification meta entries in head(). */}
        <meta name="google-site-verification" content="Khja1u78MB3_zfjOIPz9ki07dU-O5KYZbvJx6EU6q2c" />
        <meta httpEquiv="Permissions-Policy" content="camera=(), microphone=(), geolocation=(), interest-cohort=()" />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){try{var s=localStorage.getItem('theme');var m=window.matchMedia('(prefers-color-scheme: dark)').matches;var d=s?s==='dark':m;if(d)document.documentElement.classList.add('dark');var mt=document.querySelector('meta[name=\"theme-color\"]');if(mt)mt.setAttribute('content',d?'#14181A':'#f5f1e8');}catch(e){}})();",
          }}
        />
        <HeadContent />
      </head>
      <body>
        {children}
        <ConsentBanner initialConsent={consent} />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const isEnHost = Route.useLoaderData()?.isEnHost ?? false;
  // Retained source tree is gated off for this independent Turkish project.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isEnRoot =
    SERVE_ENGLISH_SITE && (isEnDomainPath(pathname) || (isEnHost && (pathname === "/" || pathname === "")));

  // The redesigned Turkish homepage renders its own editorial header.
  const isTrHome = !isEnHost && (pathname === "/" || pathname === "");
  // The admin workspace (and its dev preview) uses its own minimal chrome.
  const isWorkspace = pathname.startsWith("/admin") || pathname.startsWith("/yonetim-onizleme");

  if (isEnRoot) {
    return (
      <QueryClientProvider client={queryClient}>
        <LangProvider>
          <PageViewTracker />
          <EnShell>
            <Outlet />
          </EnShell>
        </LangProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LangProvider>
        <PageViewTracker />
        <div className={`min-h-screen flex flex-col bg-paper text-ink ${isWorkspace ? "" : "tr-home-light"}`}>
          {!isTrHome && !isWorkspace && <PublicHeader />}
          <main className="flex-1">
            <Outlet />
          </main>
          {!isWorkspace && <PublicFooter />}
        </div>
      </LangProvider>
    </QueryClientProvider>
  );
}

function PageViewTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Any match resolving to not-found (route-level or global) means this URL
  // has no content — it must stay out of analytics.
  const isNotFound = useRouterState({
    select: (s) =>
      s.matches.some(
        (m) =>
          m.status === "notFound" ||
          Boolean((m as unknown as { globalNotFound?: boolean }).globalNotFound),
      ),
  });
  const isFirst = useRef(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isNotFound) return;
    if (pathname.startsWith("/admin") || pathname.startsWith("/api/")) return;
    // GA4: gtag.js auto-sends the initial page_view; send subsequent SPA
    // navigations manually.
    if (isFirst.current) {
      isFirst.current = false;
    } else {
      trackAnalyticsPageView();
    }
    // Defer one tick so the 404 boundary (if any) has flagged this render:
    // missing pages must never be recorded as content.
    const timer = window.setTimeout(() => {
      try {
        if (!categoryAllowed("analytics")) return;
        if ((window as unknown as { __lv404?: boolean }).__lv404) return;
        let sid = localStorage.getItem("vsid");
        if (!sid) {
          sid = (crypto.randomUUID?.() ?? String(Date.now()) + Math.random().toString(36).slice(2))
            .replace(/-/g, "")
            .slice(0, 32);
          localStorage.setItem("vsid", sid);
        }
        const referrer = typeof document !== "undefined" ? document.referrer || undefined : undefined;
        const body = JSON.stringify({ path: pathname, session_id: sid, referrer });
        // Use sendBeacon when available; fall back to fetch keepalive.
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/api/public/pageview", new Blob([body], { type: "application/json" }));
        } else {
          fetch("/api/public/pageview", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {}
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pathname, isNotFound]);
  return null;
}
